/**
 * 폼 입력 컴포넌트 모음
 *
 * isEditing(편집 모드) 여부에 따라 활성/비활성 스타일을 일관되게 적용한다.
 * disabled={true} → 읽기 전용 스타일, disabled={false} → 편집 가능 스타일.
 *
 * 세 가지 variant를 named export로 제공한다:
 *   - InputField   : <input> (text / url / number / email)
 *   - TextareaField: <textarea>
 *   - SelectField  : <select> (커스텀 화살표 아이콘 포함)
 */

import { ReactNode, ChangeEvent } from 'react';

/** 편집 가능 상태의 border·focus 클래스 */
const EDIT_CLS = 'border-primary/50 focus:border-primary focus:ring-2 focus:ring-primary-light outline-none';
/** 읽기 전용(비활성) 상태의 border·bg 클래스 */
const VIEW_CLS = 'border-gray-300 bg-bg-light text-gray-500';

/**
 * select 의 기본 OS 드롭다운 화살표를 숨기고 커스텀 SVG 화살표를 background-image로 삽입.
 * appearance-none 과 함께 사용한다.
 */
export const SELECT_ARROW_STYLE = {
  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
  backgroundPosition: 'right 0.75rem center',
  backgroundSize: '1.5em 1.5em',
};

/** isEditing 여부에 따라 활성/비활성 클래스를 조합해 반환 */
export function fieldCls(isEditing: boolean, extra = '') {
  return `w-full px-4 py-3 border rounded-lg transition-colors ${isEditing ? EDIT_CLS : VIEW_CLS} ${extra}`.trim();
}

// ── 공통 Wrapper ────────────────────────────────────────────

interface LabelProps {
  label: ReactNode;
  hint?: string;
  className?: string;
}

/** label + 자식 컨트롤 + hint 텍스트를 감싸는 래퍼 */
function FieldWrapper({ label, hint, className, children }: LabelProps & { children: ReactNode }) {
  return (
    <div className={className}>
      {/* label이 빈 문자열('')이면 라벨 영역을 렌더링하지 않음 */}
      {label !== '' && (
        <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>
      )}
      {children}
      {hint && (
        <p className="mt-2 text-xs text-gray-500">
          <i className="fas fa-info-circle mr-1"></i>
          {hint}
        </p>
      )}
    </div>
  );
}

// ── InputField ──────────────────────────────────────────────

interface InputFieldProps extends LabelProps {
  type?: 'text' | 'url' | 'number' | 'email';
  value: string | number;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  placeholder?: string;
  /** input 엘리먼트에 직접 추가할 Tailwind 클래스 */
  inputClassName?: string;
}

export function InputField({
  label, hint, className,
  type = 'text', value, onChange, disabled = false, placeholder, inputClassName = '',
}: InputFieldProps) {
  return (
    <FieldWrapper label={label} hint={hint} className={className}>
      <input
        type={type}
        value={value}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
        className={`${fieldCls(!disabled)} ${inputClassName}`}
      />
    </FieldWrapper>
  );
}

// ── TextareaField ───────────────────────────────────────────

interface TextareaFieldProps extends LabelProps {
  value: string;
  onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  disabled?: boolean;
  placeholder?: string;
  rows?: number;
  inputClassName?: string;
}

export function TextareaField({
  label, hint, className,
  value, onChange, disabled = false, placeholder, rows = 4, inputClassName = '',
}: TextareaFieldProps) {
  return (
    <FieldWrapper label={label} hint={hint} className={className}>
      {/* whitespace-pre-wrap: 줄바꿈·공백을 그대로 유지해 공지사항 내용 표시에 적합 */}
      <textarea
        value={value}
        onChange={onChange}
        disabled={disabled}
        rows={rows}
        placeholder={placeholder}
        className={`${fieldCls(!disabled)} whitespace-pre-wrap ${inputClassName}`}
      />
    </FieldWrapper>
  );
}

// ── SelectField ─────────────────────────────────────────────

interface SelectFieldProps extends LabelProps {
  value: string;
  onChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
  disabled?: boolean;
  children: ReactNode;
}

export function SelectField({
  label, hint, className,
  value, onChange, disabled = false, children,
}: SelectFieldProps) {
  return (
    <FieldWrapper label={label} hint={hint} className={className}>
      <select
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={fieldCls(!disabled, 'pr-10 appearance-none bg-no-repeat')}
        style={SELECT_ARROW_STYLE}
      >
        {children}
      </select>
    </FieldWrapper>
  );
}

// 기본 export — 이전 코드와의 호환을 위해 InputField를 그대로 노출
export default InputField;
